import { Archive, ArrowLeft, CheckCircle2, FileSignature, Pencil, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DetailColumns } from '@/components/ui/detail-columns';
import type { ApiError } from '@/core/api';
import { AttachmentsPanel } from '@/features/documents';
import { AuditTimelinePanel } from '@/features/audit';
import { CommentsPanel } from '@/features/comments';
import { useCompany } from '@/features/crm/companies/queries';
import { formatMoney } from '@/lib/money';
import { AddLineForm } from '../components/AddLineForm';
import { RejectProposalDialog } from '../components/RejectProposalDialog';
import { AcceptProposalDialog } from '../components/AcceptProposalDialog';
import { statusLabel, statusVariant } from '../constants';
import {
  useAcceptProposal,
  useArchiveProposal,
  useCreateContractFromProposal,
  useProposal,
  usePurgeProposal,
  useRestoreProposal,
} from '../queries';

export function ProposalDetailPage() {
  const { publicId = '' } = useParams();
  const proposalId = publicId;
  const navigate = useNavigate();

  const { data: proposal, isPending, isError, error } = useProposal(proposalId);
  const company = useCompany(proposal?.company ?? -1);
  const archive = useArchiveProposal();
  const restore = useRestoreProposal();
  const purge = usePurgeProposal();
  const accept = useAcceptProposal();
  const [acceptOpen, setAcceptOpen] = useState(false);
  const createContract = useCreateContractFromProposal();

  async function handleCreateContract() {
    if (!proposal) return;
    const contract = await createContract.mutateAsync(proposal.id);
    navigate(`/crm/contracts/${contract.public_id}`);
  }

  async function handleArchive() {
    await archive.mutateAsync(proposalId);
    navigate('/crm/proposals');
  }

  function handleRestore() {
    restore.mutate(proposalId);
  }

  function handlePurge() {
    purge.mutate(proposalId, { onSuccess: () => navigate('/crm/proposals') });
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/proposals')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/proposals')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{proposal.title}</h1>
          <Badge variant={statusVariant(proposal.status)}>{statusLabel(proposal.status)}</Badge>
        </div>
        <div className="flex gap-2">
          {proposal.status !== 'accepted' && proposal.status !== 'rejected' ? (
            <>
              <Button
                variant="outline"
                onClick={handleCreateContract}
                disabled={createContract.isPending}
              >
                <FileSignature /> {createContract.isPending ? 'Drafting…' : 'Create contract'}
              </Button>
              <Button onClick={() => setAcceptOpen(true)} disabled={accept.isPending}>
                <CheckCircle2 /> {accept.isPending ? 'Accepting…' : 'Accept'}
              </Button>
              <RejectProposalDialog proposalId={proposal.id} dealId={proposal.deal} />
            </>
          ) : null}
          <Button variant="outline" onClick={() => navigate(`/crm/proposals/${proposal.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive proposal">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this proposal?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide this row from the active list. You can restore it later from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archive.isPending}>
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {proposal.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="text-sm">
              <p className="font-medium">This proposal is archived.</p>
              <p className="text-muted-foreground">
                Restore it to make it visible in the active list again.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={restore.isPending}
                aria-label="Restore proposal"
              >
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={proposal.title}
                onPurge={handlePurge}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {proposal.status === 'rejected' ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-4 text-sm">
            <p className="font-medium">This proposal was marked lost.</p>
            <p className="text-muted-foreground mt-1 whitespace-pre-line">
              {proposal.rejection_reason || 'No reason was recorded.'}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium uppercase">Company</p>
                  <p className="text-foreground mt-1">{company.data?.name ?? '…'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Subtotal ({proposal.currency})</p>
                  <p className="text-foreground mt-1 tabular-nums">
                    {formatMoney(proposal.subtotal, proposal.currency) ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase">Total (USD base)</p>
                  <p className="text-foreground mt-1 font-semibold tabular-nums">
                    {formatMoney(proposal.base_total, 'USD') ?? '—'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Line items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit rate</TableHead>
                        <TableHead className="text-right">Line total</TableHead>
                        <TableHead className="hidden text-right sm:table-cell">
                          Base (USD)
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {proposal.lines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                            No line items yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        proposal.lines.map(line => (
                          <TableRow key={line.id}>
                            <TableCell>{line.description || '—'}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {Number(line.quantity)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatMoney(line.unit_rate, line.currency) ?? '—'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatMoney(line.line_total, line.currency) ?? '—'}
                            </TableCell>
                            <TableCell className="hidden text-right tabular-nums sm:table-cell">
                              {formatMoney(line.base_total, 'USD') ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add a line</CardTitle>
              </CardHeader>
              <CardContent>
                <AddLineForm proposalId={proposal.id} defaultCurrency={proposal.currency} />
              </CardContent>
            </Card>

            <AttachmentsPanel modelLabel="crm.proposal" objectId={proposal.id} />

            <CommentsPanel modelLabel="crm.proposal" objectId={proposal.id} />
          </>
        }
        aside={
          <>
            <AuditTimelinePanel entity="crm.proposal" entityId={proposal.id} />
          </>
        }
      />
      {proposal.status !== 'accepted' && proposal.status !== 'rejected' ? (
        <AcceptProposalDialog
          open={acceptOpen}
          onOpenChange={setAcceptOpen}
          proposal={proposal}
          onAcceptedWithProject={(projectId) => navigate(`/projects/${projectId}`)}
        />
      ) : null}
    </div>
  );
}
